/** @jsx h */

import { Fragment } from 'preact';
import { useState, useRef, useEffect } from 'preact/hooks';
import {
  endpoint,
  autoSuggest,
  searchType,
  showFacets,
  restrictionFilters,
  labels,
  headingLevelOne,
} from '../config';
import { fetchSearchData, replaceUrl } from '../helpers/helpers';
import { searchStore } from '../store/store';
import FilterContainer from '../components/FilterContainer';
import ResultContainer from '../components/ResultContainer';
import FilterRemoveAll from '../components/FilterRemoveAll';
import Loading from '../components/Loading';

const SearchGlobal = ({ initialSearchData, pluginNamespace }) => {
  // set default states
  const [searchData, setSearchData] = useState(initialSearchData);
  const [isLoading, setIsLoading] = useState(false);
  const [isError, setIsError] = useState(false);
  const [isReset, setIsReset] = useState(false);
  const [resultIsEmpty, setResultIsEmpty] = useState(
    searchData.allResultCount === 0,
  );
  const [inputQuery, setInputQuery] = useState(searchData.arguments);
  const [resultList, setResultList] = useState(
    searchData.documents.list.results,
  );
  const formEl = useRef(null);
  const paginationInputEl = useRef(null);

  // get setSearchJson function from the store
  const setSearchJson = searchStore(state => state.setSearchJson);

  function resetInput(selector) {
    formEl.current.querySelectorAll(selector).forEach((input) => {
      if (input.checked) input.checked = false;
      if (input.value) input.value = '';
    });
  }

  function resetFilters(keys = [], exclude = []) {
    const form = formEl.current;
    const formData = new FormData(form);

    if (keys?.length > 0) {
      keys.forEach((key) => {
        resetInput(`[name='${key}']`);
      });

      triggerSearch();

      return;
    }

    setIsReset(true);

    // if it's a new query, we need to reset all previous facets
    for (const pair of formData.entries()) {
      const key = pair[0];
      const value = pair[1];
      // clear all filter except input query and restrictionFilters
      if (!exclude.includes(key) && !restrictionFilters.includes(value)) {
        resetInput(`[name='${key}']`);
      }
    }

    triggerSearch();
  }

  function handleSubmit(e) {
    e && e.preventDefault();
    triggerSearch();
  }

  function handleLoadMore() {
    triggerSearch(true);
  }

  function triggerSearch(isLoadMore = false, query = '') {
    const formData = new FormData(formEl.current);

    // update query, if one was passed into this function
    if (query) formData.set(`${pluginNamespace}[q]`, query);
    const formQuery = formData.get(`${pluginNamespace}[q]`);

    // reset filters if user is searching with a new query
    // if (formQuery && inputQuery) {
    //   const isNewQuery = formQuery !== inputQuery;
    //   if (isNewQuery) resetFilters(formData);
    // } else if ((formQuery && !inputQuery) || (!formQuery && inputQuery)) {
    //   resetFilters(formData);
    // }

    setInputQuery(formQuery);

    // if (formData.get(`${pluginNamespace}[q]`) === '') {
    //   formData.delete(`${pluginNamespace}[q]`);
    // }
    if (formData.get(`${pluginNamespace}[sort]`) === '') {
      formData.delete(`${pluginNamespace}[sort]`);
    }

    if (!isLoadMore && formData.get(`${pluginNamespace}[page]`)) {
      formData.delete(`${pluginNamespace}[page]`);
    }

    getSearchData(formData, isLoadMore);
  }

  function getSearchData(formData, isLoadMore = false) {
    setIsLoading(true);
    setIsError(false);
    fetchSearchData(endpoint, formData)
      .then(({ data }) => {
        const resultData = data.content.colPos0[0].content.data;
        const hasNoResults = resultData.allResultCount === 0;
        const resultDataList = resultData.documents.list.results;

        // if there are no results, show no-result text
        if (hasNoResults) {
          setResultIsEmpty(true);
          setResultList([]);
          setSearchJson(resultData);
          setSearchData(resultData);
        } else {
          setResultIsEmpty(false);
          setSearchData(resultData);

          if (isLoadMore) {
            // append next item into array, when is loadmore (cause we dont want to replace the prev items)
            setResultList([...resultList, ...resultDataList]);

            /*
             * onLoadMore, we dont want to replace the whole searchJson (from the store)
             * We only need to update the pagination number and the result count
             * and append the new incoming result list to the old one
             */
            const { searchJson } = searchStore.getState();
            const searchJsonCount = searchJson.documents.list.count;

            // append new result to the old result
            searchJson.documents.list.results = [
              ...resultList,
              ...resultDataList,
            ];

            // update searchJson count
            searchJson.documents.list.count = searchJsonCount + resultData.documents.list.count;

            // update current pagination
            searchJson.pagination.current = resultData.pagination.current;

            // save it in our searchStore (needed for popstate later)
            setSearchJson(searchJson);
          } else {
            // replace the whole list
            setResultList(resultDataList);

            // save response json in our searchStore (needed for popstate later)
            setSearchJson(resultData);
          }
        }

        // add search params to the url
        replaceUrl(
          process.env.NODE_ENV === 'production' ? endpoint : '/api/v1/search',
          resultData.currentSearch,
          formData,
          pluginNamespace,
        );

        setIsLoading(false);
        setIsReset(false);
      })
      .catch((error) => {
        console.error('Search Error:', error);
        setIsLoading(false);
        setIsError(true);
        setIsReset(false);
      });
  }

  // Some facets are mandatory, they cannot be cancelled:
  // - restrictionFilters always stay the same
  // This is why we must not render these mandatory filters as active facet deselect button.
  function getVisibleActiveFacets() {
    let activeFacetsWithoutMandatoryFilters = [];
    if (restrictionFilters.length) {
      const mandatoryFilter = [];
      [...restrictionFilters].forEach((item) => {
        const [mandatoryFilterName, mandatoryFilterValue] = item.split(':');
        const obj = {
          facet: '',
          name: mandatoryFilterName,
          item: mandatoryFilterValue,
        };
        mandatoryFilter.push(obj);
      });
      activeFacetsWithoutMandatoryFilters = searchData.facetsActive.selected.filter(
        item => !mandatoryFilter.some(facet => item.name === facet.name),
      );
    } else {
      activeFacetsWithoutMandatoryFilters = searchData.facetsActive.selected;
    }

    searchData.facetsActive.selected = activeFacetsWithoutMandatoryFilters;

    return searchData.facetsActive;
  }

  function getRestrictionFiltersAsObj() {
    const obj = {};

    restrictionFilters
      && restrictionFilters.forEach((filter) => {
        const splitArray = filter.split(':');
        const key = splitArray[0];
        const value = splitArray[1];

        if (key && value) obj[key] = value;
      });

    return obj;
  }

  return (
    <Fragment>
      {/* show loading animation, while fetching */}
      {isLoading && <Loading />}
      <div class="search__inner">
        <form
          className="search-form"
          action="#"
          onSubmit={handleSubmit}
          ref={formEl}
          data-suggest={autoSuggest}
          aria-labelledby="search-form"
        >
          <div className="search-form__wrapper">
            <div className="search-form__inner">
              <div className="search-form__header">
                {headingLevelOne ? (
                  <h1 id="search-form" class="headline headline--module-2">{labels.search.header}</h1>
                ) : (
                  <h2 id="search-form" class="headline headline--module-2">{labels.search.header}</h2>
                )}
                {labels.search.subheader && (
                  <div class="search__text">{labels.search.subheader}</div>
                )}
              </div>
              {restrictionFilters.length
                ? restrictionFilters.map((restrictionFilter, key) => (
                    <input
                      key={key}
                      id={`${restrictionFilter}`}
                      className="is-hidden"
                      type="checkbox"
                      name={`${pluginNamespace}[filter][]`}
                      value={`${restrictionFilter}`}
                      data-label={''}
                      checked="checked"
                    />
                ))
                : ''}
              {showFacets && (
                <FilterContainer
                  query={inputQuery}
                  facets={searchData.facets}
                  facetsActive={searchData?.facetsActive}
                  triggerAjaxSubmit={handleSubmit}
                  pluginNamespace={pluginNamespace}
                  isReset={isReset}
                />
              )}
              <input
                type="text"
                name={`${pluginNamespace}[page]`}
                hidden
                readOnly
                ref={paginationInputEl}
              />
              {searchData.facetsActive && (
                <FilterRemoveAll
                  resetFilters={resetFilters}
                />
              )}
            </div>
          </div>
        </form>
        <ResultContainer
          searchType={searchType}
          allResultCount={searchData.allResultCount}
          list={resultList}
          listCount={searchData.documents.list.count}
          pagination={searchData.documents.pagination}
          loadMore={handleLoadMore}
          pluginNamespace={pluginNamespace}
          isError={isError}
          resultIsEmpty={resultIsEmpty}
          paginationInputEl={paginationInputEl}
          inputQuery={inputQuery}
          restrictionFilters={getRestrictionFiltersAsObj()}
        />
      </div>
    </Fragment>
  );
};

export default SearchGlobal;
